import { Box, ButtonBase, Container } from '@mui/material';
import { useState } from 'react';

import { routes } from '@/assets/constants/routes-no.ts';
import { CompetitionWinnersDialog } from '@/components/CompetitionWinnersDialog/index.ts';
import { LoanExampleText } from '@/components/LoanExampleText/index.ts';
import { sendUserPortalEvent } from '@/lib/analytics/unified.ts';
import type { CompetitionWinner, CompetitionWithConfig } from '@/lib/api/requests.ts';
import { NumberCircle } from '@/ui/NumberCircle.tsx';
import { NumberedList, NumberedListItem } from '@/ui/NumberedList.tsx';

import { Animation } from '../_assets/Animation.tsx';
import Glow1 from '../_assets/glow-1.svg?react';
import { CTA } from '../_components/CTA.tsx';
import { useHasEnded } from '../_components/useHasEnded.ts';

export const TopSection = (props: {
  activeCompetition: CompetitionWithConfig | undefined;
  winners: CompetitionWinner[] | undefined;
}) => {
  return (
    <>
      <div className="relative isolate flex w-full flex-col overflow-hidden">
        <Background />

        <Container maxWidth="xl">
          <Content activeCompetition={props.activeCompetition} winners={props.winners} />
        </Container>
      </div>

      <Disclaimer />
    </>
  );
};

const Background = () => (
  <div className="absolute inset-0 isolate -z-10 flex flex-col overflow-hidden bg-[#031839]">
    <Glow1 className="absolute h-full w-full" preserveAspectRatio="xMidYMid slice" />
  </div>
);

const Disclaimer = () => (
  <div className="bg-white pt-4">
    <Container maxWidth="md">
      <div className="text-center typography-v1-body4 text-balance">
        <LoanExampleText />
      </div>
    </Container>
  </div>
);

const Content = (props: {
  activeCompetition: CompetitionWithConfig | undefined;
  winners: CompetitionWinner[] | undefined;
}) => {
  const ended = useHasEnded();
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="pt-14 pb-16 text-white md:text-center">
      <div className="relative isolate mx-auto flex flex-col md:max-w-3xl lg:min-h-[530px]">
        <Animation className="absolute -right-4 bottom-32 -z-10 origin-bottom-right scale-40 md:bottom-16 md:scale-60 lg:-right-50 lg:bottom-32 lg:scale-75 xl:-right-64 xl:bottom-4 xl:scale-100 2xl:-right-80 2xl:bottom-16" />

        <h1 className="mt-auto mb-1 text-4xl leading-[1.2] font-bold max-xl:text-balance sm:text-5xl xl:text-6xl">
          Gjett om du kan få lavere rente med <span className="text-[#18A0FB]">Scoopr</span>
        </h1>
        <Box
          component="p"
          sx={{
            mb: { xs: 3, md: 4, lg: 3 },
            fontWeight: 700,
          }}
          className="typography-v1-h3 text-balance"
        >
          Delta om å få slettet inntil{' '}
          <span className="font-bold text-[#FDC564]">500&nbsp;000&nbsp;kr </span>
          av din forbruks- og kredittkortgjeld.
        </Box>

        <p className="mb-2 typography-v1-h4">For å delta:</p>
        <div className="flex flex-col md:items-center">
          <NumberedList
            sx={{
              maxWidth: { xs: 250, sm: 10000 },
            }}
            renderNumber={(props) => (
              <NumberCircle
                sx={{
                  fontSize: 14,
                  height: 24,
                  backgroundColor: '#FFFFFF59',
                  color: '#F0F4FA',
                  fontWeight: 700,
                }}
                {...props}
              />
            )}
            renderLabel={(props) => <p {...props} className="typography-v1-body3" />}
          >
            <NumberedListItem>Logg inn med BankID</NumberedListItem>
            <NumberedListItem>Gjett og søk om lavere rente</NumberedListItem>
            <NumberedListItem>Se fasiten og delta</NumberedListItem>
          </NumberedList>
        </div>

        {ended && (
          <p className="mt-6 typography-v1-h4">
            <span>Denne trekningen er avsluttet. </span>
            <ButtonBase
              onClick={() => setIsOpen(true)}
              sx={{
                color: '#74C6FD',
                textDecoration: 'underline',
                mb: 0.5,
                fontWeight: 700,
                ml: 1,
              }}
            >
              Se vinneren her
            </ButtonBase>
          </p>
        )}

        <Box sx={{ my: 3 }}>
          <a
            href={routes.SEPTEMBER_2025_COMPETITION_TERMS}
            style={{ color: 'inherit' }}
            className="underline"
            target="_blank"
          >
            <p className="typography-v1-body3">Vilkår og betingelser for deltagelse</p>
          </a>
        </Box>

        <CTA
          activeCompetition={props.activeCompetition}
          sx={{ width: 'auto', mb: 3 }}
          onClick={() => {
            sendUserPortalEvent('sep25_competition_cta_top_clicked');
          }}
        />

        <p className="mx-auto max-w-sm italic">
          Scoopr samarbeider med flere banker for å se om vi kan skaffe deg lavere rente på din
          gjeld.
        </p>

        {props.winners && (
          <CompetitionWinnersDialog
            open={isOpen}
            onClose={() => setIsOpen(false)}
            winners={props.winners}
          />
        )}
      </div>
    </div>
  );
};
